import { Co2MaterialSettingType, GetCo2MaterialAssignSettingItemResponse, SupplementalValueType, UpdateCo2MaterialAssignSettingItemRequest } from "@api/apiClient";
import { useAppSettings } from "@api/appSettings";
import { useCo2MaterialAssignSettings, useCreateCo2MaterialAssignSettingsItem, useUpdateCo2MaterialAssignSettingsItem } from "@api/co2MaterialAssignSettings";
import { useCo2MaterialTypes } from "@api/co2MaterialTypes";
import DataGrid from "@components/DataGrid/DataGrid";
import MaterialSelectDialog from "@components/MaterialSelectDialog/MaterialSelectDialog";
import i18n from "@i18n/i18n";
import ResetIcon from "@mui/icons-material/SettingsBackupRestore";
import { Box, Checkbox, Snackbar, Tooltip } from "@mui/material";
import TextField from "@mui/material/TextField";
import {
  GridActionsCellItem,
  GridCellEditStartParams,
  GridCellEditStopParams,
  GridColDef,
  GridEditModes,
  GridRowId,
  GridRowModel,
  GridRowParams,
  GridRowSelectionModel,
  GridToolbar,
  MuiEvent,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import _ from "lodash";
import { useConfirm } from "material-ui-confirm";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";

export interface RebarEstimationGridProps {
  mode: Co2MaterialSettingType;
  projectId?: string | null;
  scenarioId?: string | null;
  profileId?: string | null;
  originScreen:'Admin' | 'Project' | 'Scenario'
}

const RebarEstimationGrid = observer((props: RebarEstimationGridProps) => {
  //console.log("RebarEstimationGrid", props);
  const [rows, setRows] = useState<GetCo2MaterialAssignSettingItemResponse[]>([]);
  const [editCellId, setEditCellId] = useState<GridRowId | null>(null); // TODO: this is a rowId not an editCellId (does not exist)
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [openRebarValueToast, setOpenRebarValueToast] = useState(false);
  const [openLimitErrorToast, setOpenLimitErrorToast] = useState(false);
  const co2MaterialAssignSettings = useCo2MaterialAssignSettings(props.mode, props.projectId, props.scenarioId, props.profileId, {
    enabled:
      props.mode === Co2MaterialSettingType.Global ||
      (props.mode === Co2MaterialSettingType.Project && !!props.projectId) ||
      (props.mode === Co2MaterialSettingType.Scenario && !!props.scenarioId) ||
      (props.mode === Co2MaterialSettingType.Profile && !!props.profileId),
  });

  const isLowHighDisable = props.mode === Co2MaterialSettingType.Project || props.mode === Co2MaterialSettingType.Scenario;

  const updateCo2MaterialAssignSettingsItem = useUpdateCo2MaterialAssignSettingsItem();
  const createCo2MaterialAssignSettingsItem = useCreateCo2MaterialAssignSettingsItem();
  const co2MaterialTypes = useCo2MaterialTypes();
  const apiRef = useGridApiRef();
  const [openMaterialDlg, setOpenMaterialDlg] = useState(false);
  const materialDlgRef = useRef();
  const containerRef = useRef(null);
  const { height } = useResizeObserver({ ref: containerRef });
  const confirm = useConfirm();

  useEffect(() => {
    console.log("RebarEstimationGrid useEffect", co2MaterialAssignSettings.data);
    if (co2MaterialAssignSettings.data && co2MaterialTypes.data?.length > 0) {
      let newRows = co2MaterialAssignSettings.data?.co2MaterialAssignSettingItems?.map((row: any, index: number) => ({ ...row, id: index })) || [];
       if (appSettings.data?.profileLevelRebarSettings && props.originScreen != "Admin") {
         newRows = newRows.filter((item) => item.value != null && item.co2MaterialTypeId != null);
       }
      setRows(newRows);
    }
  }, [co2MaterialAssignSettings.data]);

  useEffect(() => {
    // Can't find an appropriate event to trigger this on
    // needs more investigation
    // A more fine grained approach would be to not update the entire rows array
    // but only an individual row at a time
    setTimeout(() => {
      restoreState();
    }, 500);
  }, [rows]);

  const handleSelectMaterial = async (selectedMaterialType?: any) => {
    saveState();
    await apiRef.current.setEditCellValue({ id: editCellId!, field: "co2MaterialTypeId", value: selectedMaterialType?.co2MaterialTypeId });
    apiRef.current.stopCellEditMode({ id: editCellId!, field: "co2MaterialTypeId", ignoreModifications: true, cellToFocusAfter: "none" });
    if (selectedMaterialType) {
      const updatedRow: any = rows.find((row: any) => row.id === editCellId);
      updatedRow.co2MaterialTypeId = selectedMaterialType.co2MaterialTypeId;
      const updateCo2MaterialAssignSettingItemRequest: UpdateCo2MaterialAssignSettingItemRequest = createPayload(updatedRow, selectedMaterialType);
      updateCo2MaterialAssignSettingItemRequest.co2MaterialAssignSettingItemId == null
        ? await createCo2MaterialAssignSettingsItem.mutateAsync(updateCo2MaterialAssignSettingItemRequest)
        : await updateCo2MaterialAssignSettingsItem.mutateAsync(updateCo2MaterialAssignSettingItemRequest);
      await co2MaterialAssignSettings.refetch();

      if (co2MaterialTypes.data?.find((type: any) => type.co2MaterialTypeId === selectedMaterialType.co2MaterialTypeId) == null) {
        await co2MaterialTypes.refetch();
      }
    }
    setOpenMaterialDlg(false);
  };

  const handleDeleteClick = async (id: GridRowId) => {
    saveState();
    const updatedRow: any = rows.find((row: any) => row.id === id);
    updatedRow.value = 0;
    updatedRow.co2MaterialTypeId = null;
    updatedRow.isRebarFilterByMaterialsEnabled = false;
    const updateCo2MaterialAssignSettingItemRequest: UpdateCo2MaterialAssignSettingItemRequest = createPayload(updatedRow);
    await updateCo2MaterialAssignSettingsItem.mutateAsync(updateCo2MaterialAssignSettingItemRequest);
  };
  const appSettings = useAppSettings();
  const isColumnEditable = (appSettings.data?.profileLevelRebarSettings && props.originScreen=="Admin" ) || !appSettings.data?.profileLevelRebarSettings;
  const processRowUpdate = async (updatedRow: GridRowModel, _originalRow: GridRowModel) => {
    const updateCo2MaterialAssignSettingItemRequest: UpdateCo2MaterialAssignSettingItemRequest = createPayload(updatedRow);

    const rebarValue = parseFloat(updateCo2MaterialAssignSettingItemRequest.value!);
    if (validateRebarValue(rebarValue, updateCo2MaterialAssignSettingItemRequest.lowLimit, updateCo2MaterialAssignSettingItemRequest.highLimit)) {
      saveState();
      updateCo2MaterialAssignSettingItemRequest.co2MaterialAssignSettingItemId == null
        ? await createCo2MaterialAssignSettingsItem.mutateAsync(updateCo2MaterialAssignSettingItemRequest)
        : await updateCo2MaterialAssignSettingsItem.mutateAsync(updateCo2MaterialAssignSettingItemRequest);

      return updatedRow;
    } else {
      setOpenRebarValueToast(true);
    }
  };

  const validateRebarValue = (rebarValue: number | undefined, lowLimit: string | undefined, highLimit: string | undefined) => {
    const low = parseFloat(lowLimit!);
    const high = parseFloat(highLimit!);

    if (_.isFinite(low) && _.isFinite(high) && low > high) {
      setOpenLimitErrorToast(true);
      return false;
    }

    if (_.isFinite(rebarValue)) {
      if (_.isFinite(low) && _.isFinite(high)) {
        return Number(rebarValue) >= low && Number(rebarValue) <= high;
      } else if (_.isFinite(low)) {
        return Number(rebarValue) >= low;
      } else if (_.isFinite(high)) {
        return Number(rebarValue) <= high;
      }
    }

    return true;
  };

  const saveState = () => {
    console.log("save state");
    let current: any = apiRef.current.exportState();
    current = {
      ...current,
      scrollParams: apiRef.current.getScrollPosition(),
    };
    localStorage.setItem("rebarEstimationGrid", JSON.stringify(current));
  };

  const restoreState = () => {
    const data = localStorage.getItem("rebarEstimationGrid");
    if (data) {
      const state = JSON.parse(data);
      console.log("restore state: ", state);
      apiRef.current?.restoreState(state);
      if (state.scrollParams) {
        apiRef.current?.scroll(state.scrollParams);
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: "cadModelCategoryName",
      headerName: i18n.t("rebar-grid.columns.category"),
      editable: false,
      flex: 1,
    },
    {
      field: "isRebarFilterByMaterialsEnabled",
      headerName: i18n.t("rebar-grid.columns.is-rebar-filter-enabled"),
      type: "boolean",
      editable: isColumnEditable,
      flex: 1,
      renderCell: (params) => (
        <Checkbox
          disabled={!isColumnEditable}
          checked={params.value}
          onChange={async (e) => {
            const newValue = e.target.checked;
            const updatedRow = { ...params.row, isRebarFilterByMaterialsEnabled: newValue };
            processRowUpdate(updatedRow, params.row);
          }}
        />
      ),
      resizable: false,
      width: 50,
    },
    {
      field: "value",
      headerName: i18n.t("rebar-grid.columns.per-rebar"),
      type: "number",
      align: "left",
      headerAlign: "left",
      editable: isColumnEditable,
      valueFormatter: (value: any) => {
        const newValue = parseFloat(value);

        if (_.isFinite(newValue)) {
          return i18n.t("percent", { value: newValue });
        } else {
          return "";
        }
      },
      renderEditCell: (params) => (
        <TextField
          className="editable-cell-input"
          type="number"
          value={params.value ?? ""}
          onChange={async (e) => {
            const newValue = parseFloat(e.target.value);

            if (_.isFinite(newValue)) {
              if (newValue >= 0 && newValue <= 100) {
                await params.api.setEditCellValue({ id: params.id, field: params.field, value: newValue.toFixed(2) });
              }
            } else {
              await params.api.setEditCellValue({ id: params.id, field: params.field, value: "" });
            }
          }}
        />
      ),
      resizable: false,
      width: 150,
    },
    {
      field: "lowLimit",
      headerName: i18n.t("rebar-grid.columns.low-limit"),
      type: "number",
      align: "left",
      headerAlign: "left",
      editable: isColumnEditable,
      valueFormatter: (value: any) => {
        const newValue = parseFloat(value);

        if (_.isFinite(newValue)) {
          return i18n.t("percent", { value: newValue });
        } else {
          return "";
        }
      },
      renderEditCell: (params) => (
        <TextField
          className="editable-cell-input"
          type="number"
          disabled={isLowHighDisable}
          value={params.value ?? ""}
          onChange={async (e) => {
            const newValue = parseFloat(e.target.value);

            if (_.isFinite(newValue)) {
              if (newValue >= 0 && newValue <= 100) {
                await params.api.setEditCellValue({ id: params.id, field: params.field, value: newValue.toFixed(2) });
              }
            } else {
              await params.api.setEditCellValue({ id: params.id, field: params.field, value: "" });
            }
          }}
        />
      ),
      resizable: false,
      width: 150,
    },
    {
      field: "highLimit",
      headerName: i18n.t("rebar-grid.columns.high-limit"),
      type: "number",
      align: "left",
      headerAlign: "left",
      editable: isColumnEditable,
      valueFormatter: (value: any) => {
        const newValue = parseFloat(value);

        if (_.isFinite(newValue)) {
          return i18n.t("percent", { value: newValue });
        } else {
          return "";
        }
      },
      renderEditCell: (params) => (
        <TextField
          className="editable-cell-input"
          type="number"
          disabled={isLowHighDisable}
          value={params.value ?? ""}
          onChange={async (e) => {
            const newValue = parseFloat(e.target.value);

            if (_.isFinite(newValue)) {
              if (newValue >= 0 && newValue <= 100) {
                await params.api.setEditCellValue({ id: params.id, field: params.field, value: newValue.toFixed(2) });
              }
            } else {
              await params.api.setEditCellValue({ id: params.id, field: params.field, value: "" });
            }
          }}
        />
      ),
      resizable: false,
      width: 150,
    },
    {
      field: "co2MaterialTypeId",
      headerName: i18n.t("rebar-grid.columns.co2-material-type"),
      editable: isColumnEditable,
      flex: 1,
      renderCell: (params: any) => {
        if (co2MaterialTypes.data?.length > 0) {
          const co2MaterialType = co2MaterialTypes.data?.find((type: any) => type.co2MaterialTypeId === params.row?.co2MaterialTypeId);
          return co2MaterialType?.name;
        } else {
          return params.row?.co2MaterialTypeName;
        }
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: i18n.t("rebar-grid.columns.actions"),
      resizable: false,
      width: 100,
      getActions: (params: GridRowParams) => {
        if (params.row?.lowLimit && parseFloat(params.row?.lowLimit) > 0) {
          return [];
        } else {
          return [
            <GridActionsCellItem
              disabled={!isColumnEditable}
              key={`reset-${params.id}`}
              icon={
                <Tooltip title={i18n.t("rebar-estimation-grid.reset-button.tooltip")}>
                  <ResetIcon />
                </Tooltip>
              }
              label={i18n.t("rebar-estimation-grid.reset-button.title")}
              onClick={() => handleDeleteClick(params.id)}
            />,
          ];
        }
      },
    },
  ];

  function createPayload(rowObj: any, selectedMaterialType: any = null): UpdateCo2MaterialAssignSettingItemRequest {
    return new UpdateCo2MaterialAssignSettingItemRequest({
      co2MaterialAssignSettingItemId: rowObj.co2MaterialAssignSettingsItemId,
      co2MaterialAssignSettingId: rowObj.co2MaterialAssignSettingId,
      value: rowObj.value,
      valueType: SupplementalValueType.Percent,
      hasCo2MaterialType: !!selectedMaterialType,
      co2MaterialTypeId: rowObj.co2MaterialTypeId || selectedMaterialType?.co2MaterialTypeId,
      cadModelCategoryId: rowObj.cadModelCategoryId || selectedMaterialType?.cadModelCategoryId,
      co2MaterialLibraryId: rowObj.co2MaterialLibraryId || selectedMaterialType?.co2MaterialLibraryId,
      externalId: rowObj.externalId || selectedMaterialType?.externalId,
      name: rowObj.co2MaterialTypeName || selectedMaterialType?.name,
      sourceData: JSON.stringify(selectedMaterialType),
      lowLimit: rowObj.lowLimit,
      highLimit: rowObj.highLimit,
      isRebarFilterByMaterialsEnabled: rowObj.isRebarFilterByMaterialsEnabled,
    } as UpdateCo2MaterialAssignSettingItemRequest);
  }

  console.log("height", height);

  return (
    <Box ref={containerRef} sx={{ width: "100%", height: "100%" }}>
      <DataGrid
        loading={co2MaterialAssignSettings.isFetching || co2MaterialTypes.isFetching || updateCo2MaterialAssignSettingsItem.isPending}
        apiRef={apiRef}
        columns={columns}
        rows={rows}
        disableColumnMenu
        hideFooter={false}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => console.error("onProcessRowUpdateError: ", error)}
        onCellEditStart={(params: GridCellEditStartParams, _event: MuiEvent) => {
          if (params.field === "co2MaterialTypeId") {
            setEditCellId(params.id);
            setOpenMaterialDlg(true);
          }
        }}
        onCellDoubleClick={(params, event) => {
          if (params.field === "isRebarFilterByMaterialsEnabled") {
            event.stopPropagation();
          }
        }}
        onCellEditStop={(_params: GridCellEditStopParams, event: MuiEvent) => {
          // When the material dialog is open,
          // prevent the default edit stop behavior when clicking outside of the edit cell
          if (openMaterialDlg) {
            event.defaultMuiPrevented = true;
          }
        }}
        initialState={{
          sorting: {
            sortModel: [{ field: "cadModelCategoryName", sort: "asc" }],
          },
        }}
        //checkboxSelection
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setRowSelectionModel(newRowSelectionModel);
        }}
        rowSelectionModel={rowSelectionModel}
        sx={{ height: `${(height || 400) - 16}px` }}
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
      />
      {openMaterialDlg && (
        <MaterialSelectDialog
          ref={materialDlgRef}
          open={openMaterialDlg}
          onClose={() => handleSelectMaterial()}
          onSubmit={(selectedMaterialType: any) => handleSelectMaterial(selectedMaterialType)}
        />
      )}
      <Snackbar
        open={openRebarValueToast}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        message={i18n.t("rebar-grid.delete-dialog.rebar-snackbar")}
        onClose={() => setOpenRebarValueToast(false)}
      />

      <Snackbar
        open={openLimitErrorToast}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        message={i18n.t("rebar-grid.delete-dialog.rebar-limit-snackbar")}
        onClose={() => setOpenLimitErrorToast(false)}
      />
    </Box>
  );
});

export default RebarEstimationGrid;
